// src/components/AllSubSubCategoryBoxes.jsx
import React from "react";
import { Button, Col, Row, Tag, Typography } from "antd";
import { useNavigate } from "react-router-dom";

const { Title, Paragraph } = Typography;

const AllSubSubCategoryBoxes = ({ slug, subProducts = [], activeSub }) => {
  const navigate = useNavigate();

  const activeSubProduct = subProducts?.find((s) => s.slug === activeSub);
  if (!activeSubProduct) return null;

  const details = activeSubProduct.product_details || [];
  const BACKEND_URL = process.env.REACT_APP_API_URL;

  // ✅ Format inline text (bold, italic, links)
  const formatChildren = (children = []) =>
    children
      .map((c) => {
        if (c.type === "text") {
          let t = c.text || "";
          if (c.bold) t = `<strong>${t}</strong>`;
          if (c.italic) t = `<em>${t}</em>`;
          if (c.underline) t = `<u>${t}</u>`;
          return t;
        }
        if (c.type === "link") {
          const inner = formatChildren(c.children || []);
          return `<a href="${c.url}" target="_blank" rel="noopener noreferrer">${inner}</a>`;
        }
        return "";
      })
      .join("");

  // ✅ MAIN FIX: Proper HTML reconstruction
  const renderBlocks = (blocks) => {
    if (!Array.isArray(blocks)) return null;

    let htmlParts = [];

    blocks.forEach((b) => {
      if (b?.type === "code") {
        const text = (b.children || [])
          .map((c) => c.text || "")
          .join("");

        htmlParts.push(text); // ❗ DO NOT trim
      }
    });

    // ✅ If HTML exists → render full HTML (table etc.)
    if (htmlParts.length > 0) {
      const fullHTML = htmlParts.join(" "); 

      return (
        <div className="richText overflow-x-auto my-4">
          <div
            dangerouslySetInnerHTML={{
              __html: fullHTML,
            }}
          />
        </div>
      );
    }

    // ✅ Fallback for normal content
    return blocks.map((b, i) => {
      const html = formatChildren(b?.children || []);
      if (!html.trim()) return null;

      if (b?.type === "heading") {
        const Tag = `h${b?.level || 3}`;
        return React.createElement(Tag, {
          key: i,
          className: "mb-3",
          dangerouslySetInnerHTML: { __html: html },
        });
      }

      if (b?.type === "list") {
        const items = (b?.children || [])
          .map((li) => `<li>${formatChildren(li?.children || [])}</li>`)
          .join("");

        return (
          <ul
            key={i}
            className="list-disc pl-6 mb-3 text-sm"
            dangerouslySetInnerHTML={{ __html: items }}
          />
        );
      }

      return (
        <p
          key={i}
          className="mb-3 text-sm"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    });
  };

  return (
    <div className="container">
      {/* Header */}
      <div className="flex items-center mb-6 font-medium">
        <div>
          <Title
            className="!font-medium !text-danger !text-2xl !mb-1"
            level={2}
          >
            <span className="text-primary">Explore</span>{" "}
            {activeSubProduct.title}
          </Title>

          <div className="richText">
            {renderBlocks(activeSubProduct.description)}
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      <Row gutter={[20, 40]}>
        {details.map((detail, index) => (
          <Col xs={24} md={6} key={index}>
            <div
              onClick={() =>
                navigate(`/${activeSubProduct.slug}/${detail.slug}`)
              }
              className="cursor-pointer h-full bg-white rounded-xl overflow-hidden relative shadow-lg hover:shadow-2xl transition-all"
            >
              {/* Image */}
              <div className="relative">
                <div className="absolute bg-black/20 w-full h-full">
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                    {detail.final_price && (
                      <div className="text-base font-semibold text-white">
                        INR {detail.final_price.toLocaleString("en-IN")}
                      </div>
                    )}
                    {detail.old_price && (
                      <div className="line-through text-white text-md">
                        INR {detail.old_price.toLocaleString("en-IN")}
                      </div>
                    )}
                    {detail.save_price && (
                      <div className="text-white text-md">
                        SAVE INR {detail.save_price.toLocaleString("en-IN")}
                      </div>
                    )}
                  </div>
                </div>

                <img
                  src={`${BACKEND_URL}${
                    detail.images?.[0]?.url ||
                    "https://via.placeholder.com/300x200"
                  }`}
                  alt={detail.title}
                  className="w-full h-[170px] object-cover"
                />

                {/* Rating */}
                <div
                  style={{ backgroundColor: detail.badgeColor || "#10239E" }}
                  className="absolute bottom-[-25px] right-6 rounded-full shadow-lg text-white text-sm flex justify-center w-[80px] h-[80px]"
                >
                  <div className="text-center mt-2">
                    <div className="text-3xl">★</div>
                    <div className="text-xs">4.9 (523)</div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 pt-3">
                <div className="mt-3 mb-2 text-muted text-[11px] font-medium">
                  Tour Type
                  <Tag
                    color={detail.tagColor || "blue"}
                    className="ml-2 text-[11px]"
                  >
                    {detail.tour_type || "Standard"}
                  </Tag>
                </div>

                <div className="font-medium text-black text-md">
                  {detail.title}
                </div>

                <div className="text-primary mt-2 font-medium">
                  {detail.duration}
                </div>

                <div className="text-[11px] text-muted mt-3">
                  {detail.itinerary}
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default AllSubSubCategoryBoxes;